import { Check, ExternalLink, Eye, EyeOff, X } from 'lucide-react'
import { useState } from 'react'
import { useDeleteCSV, useUploadCSV, useUserCSVUpload } from '../api/hooks/useCSVStorage'
import { validateApiKey } from '../utils'
import { DataSourceToggle } from './DataSourceToggle'
import { CsvUpload } from './ui/csv-upload'
import { Input } from './ui/input'
import { UnifiedButton } from './ui/unified-button'

interface DataImportSectionProps {
  dataSource: 'api' | 'csv'
  apiKey: string
  onDataSourceChange: (source: 'api' | 'csv') => void
  onApiKeyChange: (apiKey: string) => void
  showDataSourceToggle?: boolean
  dataSourceToggleTitle?: string
  dataSourceToggleDescription?: string
}

export const DataImportSection = ({
  dataSource,
  apiKey,
  onDataSourceChange,
  onApiKeyChange,
  showDataSourceToggle = true,
  dataSourceToggleTitle,
  dataSourceToggleDescription
}: DataImportSectionProps) => {
  const [showApiKey, setShowApiKey] = useState(false)
  const { data: csvUpload } = useUserCSVUpload()
  const uploadCSV = useUploadCSV()
  const deleteCSV = useDeleteCSV()

  const handleCSVUpload = async (file: File) => {
    await uploadCSV.mutateAsync(file)
  }

  const handleClearCSV = async () => {
    await deleteCSV.mutateAsync()
  }

  const isApiKeyValid = apiKey ? validateApiKey(apiKey) : false

  return (
    <div className="space-y-4">
      {showDataSourceToggle && (
        <DataSourceToggle
          dataSource={dataSource}
          onDataSourceChange={onDataSourceChange}
          title={dataSourceToggleTitle}
          description={dataSourceToggleDescription}
        />
      )}

      {dataSource === 'api' && (
        <div className="border-muted space-y-4 border-l-2 pt-2 pl-4">
          <a
            className="text-muted-foreground flex cursor-pointer items-center gap-1 text-xs hover:text-white/80"
            onClick={() => window.open('https://hevy.com/settings?developer', '_blank')}
          >
            <ExternalLink className="size-4" />
            Get Your Hevy API Key
          </a>
          <div className="relative">
            <Input
              type={showApiKey ? 'text' : 'password'}
              placeholder="Enter your Hevy API key"
              value={apiKey}
              onChange={(e) => onApiKeyChange(e.target.value)}
              className={isApiKeyValid ? 'border-green-500' : apiKey ? 'border-red-500' : ''}
            />
            <UnifiedButton
              variant="ghost"
              size="sm"
              onClick={() => setShowApiKey(!showApiKey)}
              className="absolute top-1/2 right-2 h-6 w-6 -translate-y-1/2 p-0"
            >
              {showApiKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </UnifiedButton>
          </div>
          {apiKey && (
            <div className="flex items-center gap-2">
              {isApiKeyValid ? (
                <Check className="h-4 w-4 text-green-500" />
              ) : (
                <X className="h-4 w-4 text-red-500" />
              )}
              <p className={`text-xs ${isApiKeyValid ? 'text-green-500' : 'text-red-500'}`}>
                {isApiKeyValid ? 'API key is valid' : 'Invalid API key format'}
              </p>
            </div>
          )}
        </div>
      )}

      {dataSource === 'csv' && (
        <div className="border-muted space-y-4 border-l-2 pt-2 pl-4">
          <a
            className="text-muted-foreground flex cursor-pointer items-center gap-1 text-xs hover:text-white/80"
            onClick={() => window.open('https://hevy.com/settings?export', '_blank')}
          >
            <ExternalLink className="size-4" />
            Export Your Hevy Data
          </a>

          {csvUpload && (
            <div className="text-muted-foreground space-y-1 text-xs">
              <div className="flex items-center gap-2">
                <Check className="h-4 w-4 text-green-500" />
                <span>CSV file ready: {csvUpload.fileName}</span>
              </div>
              <div className="text-muted-foreground text-xs">
                Uploaded: {new Date(csvUpload.uploadDate).toLocaleDateString()}
              </div>
            </div>
          )}

          <CsvUpload
            onUpload={handleCSVUpload}
            currentFileName={csvUpload?.fileName}
            onClearCsv={handleClearCSV}
            disabled={uploadCSV.isPending || deleteCSV.isPending}
          />
        </div>
      )}
    </div>
  )
}
